import * as React from 'react';

import {Stack} from 'shells/layout/stack/stack';
import {Popover} from 'shells/popover/popover';
import {PopoverForm} from 'shells/popover-form/popover-form';
import {Radio} from 'shells/radio/radio';
import {RadioGroup} from 'shells/radio/radio-group';
import {Body} from 'shells/typography/body';

import type {
    GetProductsCategories_productCategories as ProductCategory,
    ProductCategoryDeleteInput,
} from 'nutshell-graphql-types';

import {notifyUser} from './helpers';

type Props = {
    onClose: () => void;
    anchor: HTMLElement | null | undefined;
    productCategory: ProductCategory;
    onSubmit: (input: ProductCategoryDeleteInput) => Promise<any>;
    errorSaving: string | null | undefined;
};

export type CategoryDelete = 'uncategorizeProducts' | 'deleteProducts';

export function CategoryDeletePopover(props: Props) {
    const [categoryDeleteType, setCategoryDeleteType] =
        React.useState<CategoryDelete>('uncategorizeProducts');

    const handleSubmit = () => {
        const shouldDeleteProducts = Boolean(categoryDeleteType === 'deleteProducts');
        props
            .onSubmit({
                id: props.productCategory.id,
                shouldDeleteProducts: shouldDeleteProducts,
            })
            .then((res) => {
                notifyUser(res, shouldDeleteProducts);
            })
            .then(() => {
                props.onClose();
            });
    };

    return (
        <Popover anchor={props.anchor} location='bottom' onBlur={props.onClose}>
            <PopoverForm
                title='Delete category'
                onClose={props.onClose}
                onSubmit={handleSubmit}
                saveButtonText='Yes, delete'
                saveButtonVariant='danger'
                cancelButtonText='No, don’t delete'
                width={375}
                errorSaving={Boolean(props.errorSaving)}
            >
                <div className='pb-32'>
                    <RadioGroup
                        name='categorize-or-delete-products'
                        selectedValue={categoryDeleteType}
                        // @ts-expect-error
                        onChange={(value: CategoryDelete) => setCategoryDeleteType(value)}
                        spacing={24}
                    >
                        <Radio value='uncategorizeProducts' shouldTopAlignInput={true}>
                            <Stack spacing={8}>
                                <Body color='light'>Delete {props.productCategory.name}</Body>
                                <Body color='dark'>
                                    If you choose to continue,{' '}
                                    <Body isBold={true}>
                                        {props.productCategory.numAssociatedProducts} associated
                                        product
                                        {props.productCategory.numAssociatedProducts !== 1 && 's'}
                                    </Body>{' '}
                                    will be moved to the ‘Uncategorized’ group.
                                </Body>
                            </Stack>
                        </Radio>
                        <Radio value='deleteProducts' shouldTopAlignInput={true}>
                            <Stack spacing={8}>
                                <Body color='light'>
                                    Delete {props.productCategory.name} and associated products
                                </Body>
                                <Body color='dark'>
                                    If you choose to proceed, this category and its products will no
                                    longer be available in Nutshell.
                                </Body>
                            </Stack>
                        </Radio>
                    </RadioGroup>
                </div>
                {props.errorSaving && <Body color='error'>{props.errorSaving}</Body>}
            </PopoverForm>
        </Popover>
    );
}
